// parts.tsx · v12.10.29.1 (#2179 the sign-in tab names the practice) · 2026-11-03 · VoiceSpectrum AI
// parts.tsx · v12.10.11.1 (#1462 PasswordInput: green ✓ inside the box just before Show when the second box matches) · 2026-10-09
// parts.tsx · v12.10.9.4 (#1336 no sample text in sign-in boxes) · 2026-10-07 · VoiceSpectrum AI
// parts.tsx · v12.10.9.3 (#1310 footer links - Clinicspectrum, Privacy Policy, Terms of Service; #1312 Contact support + badges below the card; #1311 NewTag ends 30 days after its release) · 2026-10-07
// v12.10.9.1: #1270 mobile label "Mobile (optional - for text-message codes)".
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1255/#1256/#1257) · 2026-10-06
// parts.tsx - the building blocks every sign-in state (L1–L13, Login_Prototype_v1_2) is made of, on the EXISTING login
// design: the left brand panel + right card of the old LoginPage (.tenant-login-* in shared-designer tenant-surface.css)
// and the prototype's card pieces (.vsl-* in styles/login.css).
//   No person is ever named here (UI principles §35) - the "who to ask" line is always CONTACT.
//   Inputs are 16px (§14); code boxes accept a pasted code; errors are role="alert" (announced).
import { ReactNode, Ref, useEffect, useRef, useState, KeyboardEvent, ClipboardEvent, FormEvent } from 'react';
import MaintenanceOverlay from '../../components/MaintenanceOverlay';

export const CONTACT = 'Please contact your office administrator.';
export const CONTACT_LC = 'please contact your office administrator.';
export const OUTAGE_MSG = 'VoiceSpectrum is temporarily unavailable. Please try again in a moment.';

// ── tenant (from the address) ─────────────────────────────────────────────────────────────────────────────────────
export function resolveTenantSlug(): string | null {
  try {
    const stored = sessionStorage.getItem('vs.tenantSlug');
    if (stored) return stored;
  } catch { /* storage blocked */ }
  const querySlug = new URLSearchParams(window.location.search).get('tenant')?.trim();
  if (querySlug) return querySlug;
  const host = window.location.host.toLowerCase();
  if (host.endsWith('.voicespectrum.ai')) {
    const parts = host.split('.');
    if (parts.length >= 3 && parts[0] !== 'app') return parts[0];
  }
  return null;
}

export function resolveTenantDisplayName(): string {
  let storedName: string | null = null;
  try { storedName = sessionStorage.getItem('vs.tenantDisplayName') ?? sessionStorage.getItem('vs.tenantName'); } catch { /* blocked */ }
  const trimmed = storedName?.trim();
  const slug = resolveTenantSlug();
  return trimmed || (slug ? slug.split(/[-_]/).filter(Boolean).map((p) => `${p.charAt(0).toUpperCase()}${p.slice(1)}`).join(' ') : 'Your workspace');
}

// ── small formatters ──────────────────────────────────────────────────────────────────────────────────────────────
export function mmss(totalSeconds: number): string {
  const s = Math.max(0, Math.ceil(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
/** "Nov 5" from an ISO date (the setup banner). */
export function shortDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
/** Re-render every `ms` while `active` - for live countdowns. */
export function useTicker(active: boolean, ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [active, ms]);
  return now;
}

// ── icons ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export function ArrowIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
export function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// ── frame: brand panel + card ─────────────────────────────────────────────────────────────────────────────────────
/** v12.10.9.3 (#1311): a NEW tag shows for 30 days after the release that added the feature, then disappears by itself
 *  (rule written in the UI principles and in Brain › Releasing new features). `since` = that release's go-live date. */
export const NEW_TAG_DAYS = 30;
export function isNewSince(since: string, now: number = Date.now()): boolean {
  const t = Date.parse(since);
  return Number.isFinite(t) && now < t + NEW_TAG_DAYS * 86_400_000;
}
export function NewTag({ since }: { since: string }) {
  return isNewSince(since) ? <span className="vsl-newtag">NEW</span> : null;
}

/** v12.10.9.3 (#1312): what sits under the sign-in card - Contact support and the compliance badges. */
export function BelowCard({ onSupport }: { onSupport: () => void }) {
  return (
    <>
      <div className="tenant-login-help">Trouble signing in? <button type="button" className="vsl-lnk" onClick={onSupport}>Contact support</button></div>
      <Badges />
    </>
  );
}

const ext = { target: '_blank', rel: 'noopener noreferrer' } as const;

export function LoginFrame({ orgName, children, below }: { orgName: string; children: ReactNode; below?: ReactNode }) {
  // v12.10.29.1 (#2179, Tushar 11/3 "didn't we fix this to display business name ... on the chrome tab"): the sign-in pages name the
  // practice on the browser tab too - the same rule as the signed-in app (Shell.tsx): the name only, a leading "Dr." dropped.
  useEffect(() => {
    const name = (orgName || '').replace(/^\s*dr\.?\s+/i, '').trim();
    if (name && name !== 'Your workspace') document.title = name;
  }, [orgName]);
  return (
    <div className="tenant-login-shell">
      <MaintenanceOverlay />
      <section className="tenant-login-hero">
        <img className="tenant-login-logo" src="/brand/svg/19_compact_lockup_light.svg" alt="VoiceSpectrum AI" />
        <div className="tenant-login-mid">
          <span className="tenant-login-live"><span className="lv" /> Live - answering calls right now</span>
          <h1>The voice <span className="tenant-login-clinic">{orgName}</span> answers with.</h1>
          <p>Your business never stops answering. Every call picked up, every caller heard, every urgent message routed - 24/7, in 30+ languages, on your existing phone line.</p>
        </div>
        {/* v12.10.9.3 (#1310): the three links open in a new tab; the version stays text */}
        <footer className="tenant-login-foot">
          <a href="https://clinicspectrum.com" {...ext}>© 2026 Clinicspectrum, Inc.</a> · <a href="https://voicespectrum.ai/legal/privacy/" {...ext}>Privacy Policy</a> · <a href="https://voicespectrum.ai/legal/terms/" {...ext}>Terms of Service</a> · Version {import.meta.env.VITE_APP_VERSION}
        </footer>
      </section>
      <section className="tenant-login-panel-wrap">
        <img className="tenant-login-mark" src="/brand/svg/19_compact_lockup_light.svg" alt="VoiceSpectrum AI" />
        <div className="tenant-login-stack">
          <div className="tenant-login-panel vsl">{children}</div>
          {below && <div className="tenant-login-below vsl">{below}</div>}
        </div>
      </section>
    </div>
  );
}

/** Org pill + title. `rule` draws the divider under it (Sign in only). `icon` puts the round state icon between them.
 *  With no field to focus, the title takes focus on mount so a screen reader announces the new state. */
export function Head({ org, title, small, rule, icon, focusTitle }: {
  org: string; title: string; small?: boolean; rule?: boolean;
  icon?: { kind: 'bad' | 'info' | 'ok'; glyph: string }; focusTitle?: boolean;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (focusTitle) ref.current?.focus(); }, [focusTitle]);
  return (
    <div className={`tenant-login-head${rule ? '' : ' vsl-head--plain'}`}>
      <span className="tenant-login-status"><span /> {org}</span>
      {icon && <div className={`vsl-ic vsl-ic--${icon.kind}`} aria-hidden="true">{icon.glyph}</div>}
      <h2 ref={ref} tabIndex={-1} className={small ? 'vsl-ttl-sm' : undefined}>{title}</h2>
    </div>
  );
}

export function Sub({ children, left }: { children: ReactNode; left?: boolean }) {
  return <p className={`vsl-sub${left ? ' vsl-sub--left' : ''}`}>{children}</p>;
}

export type BannerKind = 'bad' | 'info' | 'warn' | 'ok';
export function Banner({ kind, children }: { kind: BannerKind; children: ReactNode }) {
  return <div className={`vsl-ban vsl-ban--${kind}`} role={kind === 'bad' ? 'alert' : 'status'}>{children}</div>;
}

export function FieldError({ id, children }: { id?: string; children: ReactNode }) {
  return <div id={id} className="vsl-ferr" role="alert">{children}</div>;
}

export function Note({ children }: { children: ReactNode }) {
  return <div className="vsl-note">{children}</div>;
}

export function BackLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return <button type="button" className="vsl-back" onClick={onClick}><span aria-hidden="true">←</span> {children}</button>;
}

export function PrimaryButton({ children, disabled, busy, busyText, onClick, type = 'submit', id }: {
  children: ReactNode; disabled?: boolean; busy?: boolean; busyText?: string; onClick?: () => void; type?: 'submit' | 'button'; id?: string;
}) {
  return (
    <button id={id} className="tenant-login-submit" type={type} disabled={disabled || busy} onClick={onClick} aria-busy={busy || undefined}>
      {busy ? (busyText ?? 'Working…') : <>{children} <ArrowIcon /></>}
    </button>
  );
}

export function SecondaryButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return <button type="button" className="vsl-btn2" onClick={onClick} disabled={disabled}>{children}</button>;
}

export function Badges() {
  return (
    <div className="tenant-login-badges">
      <span><CheckIcon /> HIPAA Compliant</span>
      <span><CheckIcon /> SOC 2 Type 2</span>
      <span><CheckIcon /> ISO 27001</span>
    </div>
  );
}

/** A form that never reloads the page; `onSubmit` gets no event. */
export function Form({ onSubmit, children, label }: { onSubmit: () => void; children: ReactNode; label?: string }) {
  return (
    <form noValidate aria-label={label} onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit(); }}>
      {children}
    </form>
  );
}

// ── password field with Show / Hide ───────────────────────────────────────────────────────────────────────────────
export function PasswordInput({ id, label, value, onChange, placeholder, autoComplete, invalid, describedBy, autoFocus, inputRef, match }: {
  id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string;
  autoComplete: 'current-password' | 'new-password'; invalid?: boolean; describedBy?: string; autoFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  match?: boolean;   // v12.10.11.1 (#1462): the confirm box matches - a green ✓ just before Show (UI-PROTO-T56 match mark "1")
}) {
  const [show, setShow] = useState(false);
  return (
    <label htmlFor={id}>{label}
      <span className={`password-field${match ? ' vsl-pw-match' : ''}`}>
        <input
          ref={inputRef} id={id} type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} autoComplete={autoComplete} autoFocus={autoFocus}
          className={invalid ? 'is-err' : undefined} aria-invalid={invalid || undefined} aria-describedby={describedBy}
        />
        {match && <span className="vsl-pw-tick" role="img" aria-label="The passwords match">✓</span>}
        <button type="button" aria-label={show ? 'Hide password' : 'Show password'} aria-controls={id} onClick={() => setShow((v) => !v)}>{show ? 'Hide' : 'Show'}</button>
      </span>
    </label>
  );
}

// ── 6 code boxes: type, paste (fills every box), backspace walks back, auto-completes on the 6th digit ──────────────
export function CodeBoxes({ label, invalid, describedBy, onChange, onComplete, disabled }: {
  label: string; invalid?: boolean; describedBy?: string; disabled?: boolean;
  onChange: (code: string) => void; onComplete: (code: string) => void;
}) {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => { refs.current[0]?.focus(); }, []);

  function commit(next: string[], focusIndex: number | null) {
    setDigits(next);
    const code = next.join('');
    onChange(code);
    if (focusIndex !== null) refs.current[Math.min(5, Math.max(0, focusIndex))]?.focus();
    if (code.length === 6 && next.every((d) => d !== '')) onComplete(code);
  }

  function fillFrom(start: number, text: string) {
    const clean = text.replace(/\D/g, '').slice(0, 6);
    if (!clean) return;
    // A whole code (paste / SMS autofill) always fills from the first box.
    const from = clean.length >= 6 ? 0 : start;
    const next = [...digits];
    clean.split('').forEach((d, j) => { if (from + j < 6) next[from + j] = d; });
    commit(next, Math.min(5, from + clean.length));
  }

  function onInput(i: number, raw: string) {
    const clean = raw.replace(/\D/g, '');
    if (clean.length > 1) { fillFrom(i, clean); return; }
    const next = [...digits];
    next[i] = clean.slice(-1);
    commit(next, clean ? i + 1 : null);
  }

  function onKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      e.preventDefault();
      const next = [...digits]; next[i - 1] = '';
      commit(next, i - 1);
    } else if (e.key === 'ArrowLeft' && i > 0) { e.preventDefault(); refs.current[i - 1]?.focus(); }
    else if (e.key === 'ArrowRight' && i < 5) { e.preventDefault(); refs.current[i + 1]?.focus(); }
  }

  function onPaste(i: number, e: ClipboardEvent<HTMLInputElement>) {
    const t = e.clipboardData.getData('text') || '';
    if (t.replace(/\D/g, '').length) { e.preventDefault(); fillFrom(i, t); }
  }

  return (
    <div className={`vsl-code${invalid ? ' is-err' : ''}`} role="group" aria-label={label} aria-describedby={describedBy}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          value={d}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={i === 0 ? 6 : 1}
          aria-label={`Digit ${i + 1}`}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          onChange={(e) => onInput(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={(e) => onPaste(i, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}

// ── name + optional mobile (L10 invite, L13 profile) ──────────────────────────────────────────────────────────────
export function NameFields({ first, last, mobile, onFirst, onLast, onMobile, nameError, mobileError, firstRef, lastRef, mobileRef }: {
  first: string; last: string; mobile: string; onFirst: (v: string) => void; onLast: (v: string) => void; onMobile: (v: string) => void;
  nameError: boolean; mobileError?: string;
  firstRef?: Ref<HTMLInputElement>; lastRef?: Ref<HTMLInputElement>; mobileRef?: Ref<HTMLInputElement>;
}) {
  const firstBad = nameError && !first.trim();
  const lastBad = nameError && !last.trim();
  return (
    <>
      <div className="vsl-grid2">
        <label htmlFor="vsl-fn">First name
          <input ref={firstRef} id="vsl-fn" value={first} onChange={(e) => onFirst(e.target.value)} autoComplete="given-name" required
            className={firstBad ? 'is-err' : undefined} aria-invalid={firstBad || undefined} aria-describedby={nameError ? 'vsl-name-err' : undefined} />
        </label>
        <label htmlFor="vsl-ln">Last name
          <input ref={lastRef} id="vsl-ln" value={last} onChange={(e) => onLast(e.target.value)} autoComplete="family-name" required
            className={lastBad ? 'is-err' : undefined} aria-invalid={lastBad || undefined} aria-describedby={nameError ? 'vsl-name-err' : undefined} />
        </label>
      </div>
      {nameError && <FieldError id="vsl-name-err">Enter your first and last name.</FieldError>}
      <label htmlFor="vsl-mb">
        <span>Mobile <span className="vsl-opt-hint">(optional - for text-message codes)</span></span>
        <input ref={mobileRef} id="vsl-mb" value={mobile} onChange={(e) => onMobile(e.target.value)} inputMode="tel" autoComplete="tel"
          className={mobileError ? 'is-err' : undefined} aria-invalid={!!mobileError || undefined}
          aria-describedby={mobileError ? 'vsl-mb-err' : undefined} />
      </label>
      {mobileError && <FieldError id="vsl-mb-err">{mobileError}</FieldError>}
    </>
  );
}
